import type { ReactNode } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { formatTickerToURLFriendly } from '@benzinga/utils';

import { memberDisplayNameToProfileSlug } from '../memberProfileSlug';

export const CellThumb = ({ alt, src, round = false }: { alt: string; src?: string; round?: boolean }) => {
  if (!src) return round ? <div className="mr-2 h-9 w-9 min-w-9 max-w-9" /> : null;
  return (
    <div className="mr-2 h-9 w-9 min-w-9 max-w-9">
      <img
        alt={alt}
        className={round ? 'h-full w-full rounded-full' : 'h-auto w-full'}
        loading="lazy"
        onError={
          round
            ? ({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = '/next-assets/images/no_picture.png';
              }
            : undefined
        }
        src={src}
      />
    </div>
  );
};

export const NameCell = ({ thumb, children }: { thumb?: ReactNode; children: ReactNode }) => (
  <div className="flex flex-row items-center">
    {thumb}
    <div className="flex min-w-0 flex-col overflow-hidden">{children}</div>
  </div>
);

export const TableLink = ({
  href,
  children,
  external,
  className,
}: {
  href: string;
  children: ReactNode;
  external?: boolean;
  className?: string;
}) => {
  const classes = twMerge('overflow-hidden overflow-ellipsis text-bzblue-1000 hover:text-bzblue-600', className);
  if (external) {
    return (
      <a className={classes} href={href} rel="noreferrer" target="_blank">
        {children}
      </a>
    );
  }
  return (
    <Link className={classes} href={href}>
      {children}
    </Link>
  );
};

export const SecurityNameLink = ({ ticker, children }: { ticker?: string; children: ReactNode }) =>
  ticker ? (
    <TableLink href={`/government-trades/securities/${formatTickerToURLFriendly(ticker)}`}>{children}</TableLink>
  ) : (
    <span className="overflow-hidden overflow-ellipsis">{children}</span>
  );

export const SecurityTickerLink = ({ ticker }: { ticker?: string }) =>
  ticker ? (
    <TableLink href={`/government-trades/securities/${formatTickerToURLFriendly(ticker)}`}>{ticker}</TableLink>
  ) : (
    <span className="text-gray-500">-</span>
  );

export const MemberNameLink = ({ name, className }: { name?: string; className?: string }) =>
  name ? (
    <TableLink className={className} href={`/government-trades/members/${memberDisplayNameToProfileSlug(name)}`}>
      {name}
    </TableLink>
  ) : null;

export const SecurityNameCell = ({
  logo,
  name,
  ticker,
}: {
  logo?: string;
  name?: string;
  ticker?: string;
}) => (
  <NameCell thumb={<CellThumb alt={`${name ?? ''} logo`} src={logo} />}>
    <SecurityNameLink ticker={ticker}>{name}</SecurityNameLink>
  </NameCell>
);

export const MemberHeadshotCell = ({
  name,
  headshot,
  className,
}: {
  name?: string;
  headshot?: string;
  className?: string;
}) => (
  <NameCell thumb={<CellThumb alt={`${name ?? ''} headshot`} round src={headshot} />}>
    <MemberNameLink className={className} name={name} />
  </NameCell>
);

export const SecurityStackCell = ({ logo, name, ticker }: { logo?: string; name?: string; ticker?: string }) => {
  const slug = ticker ? formatTickerToURLFriendly(ticker) : '';
  const label = ticker ? (
    <>
      <span className="overflow-hidden text-ellipsis whitespace-nowrap font-extrabold text-bzgray-900">{ticker}</span>
      <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[11px] font-medium text-bzgray-600" title={name}>
        {name}
      </span>
    </>
  ) : (
    <span className="overflow-hidden text-ellipsis whitespace-nowrap font-extrabold text-bzgray-900" title={name}>
      {name}
    </span>
  );
  return (
    <div className="flex flex-row items-center">
      <CellThumb alt={`${name} logo`} src={logo} />
      {slug ? (
        <Link className="flex min-w-0 flex-col leading-tight hover:[&>span:first-child]:text-bzblue-600" href={`/government-trades/securities/${slug}`}>
          {label}
        </Link>
      ) : (
        <span className="flex min-w-0 flex-col leading-tight">{label}</span>
      )}
    </div>
  );
};

export const TxnSide = ({ type, children }: { type?: string; children: ReactNode }) => {
  const isBuy = (type ?? '').toUpperCase().startsWith('P');
  return <span className={clsx('font-bold', isBuy ? 'text-[#2fb344]' : 'text-[#e5484d]')}>{children}</span>;
};

export const ReturnChip = ({ value }: { value?: number | null }) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-md px-3 py-1 text-sm font-semibold',
        value > 0 ? 'bg-green-700/10 text-green-700' : 'bg-red-600/10 text-red-600',
      )}
    >
      {(value * 100).toFixed(1)}%
    </span>
  );
};
